<script setup lang="ts">
import {
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonTitle,
    IonContent,
    IonNote,
    IonList,
    IonItem,
    IonLabel,
    IonCheckbox,
    IonRippleEffect,
    IonSpinner,
    IonSkeletonText,
    IonIcon,
    IonSearchbar,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    modalController,
    alertController,
} from "@ionic/vue";
import { ref, computed, PropType, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useCostumersStore } from "@/stores/costumers";
import { useRoute } from "vue-router";
import { IZone } from "@/types/zone";
import { ICostumer } from "@/types/costumer";
import { toggle } from "ionicons/icons";
import { IWeekModel } from "@/types/week";

const props = defineProps({
    onConfirm: {
        type: Function as PropType<(customer: ICostumer) => boolean | Promise<boolean>>,
        required: false,
    },
});
// Single selection (store only the selected id)
const selectedCostumerId = ref<number | null>(null);
const costumerStore = useCostumersStore();
const { isLoading } = storeToRefs(costumerStore);
const isSelected = (id: number) => selectedCostumerId.value === id;
const customers = computed(() => costumerStore.getCostumers);

// Search and incremental rendering to speed up initial load
const query = ref<string>("");
const PAGE_SIZE = 50;
const visibleCount = ref<number>(PAGE_SIZE);

const filteredCustomers = computed(() => {
    if (!query.value.trim()) return customers.value;
    const q = query.value.toLowerCase();
    return customers.value.filter((c) => {
        const name = c.business_name?.toLowerCase() ?? "";
        const address = c.address?.toLowerCase() ?? "";
        const city = c.city?.toLowerCase() ?? "";
        return name.includes(q) || address.includes(q) || city.includes(q);
    });
});

const visibleCustomers = computed(() => filteredCustomers.value.slice(0, visibleCount.value));
const canLoadMore = computed(() => visibleCount.value < filteredCustomers.value.length);
const onInfinite = async (ev: any) => {
    visibleCount.value += PAGE_SIZE;
    // Give the UI a tick before completing
    requestAnimationFrame(() => ev?.target?.complete?.());
};

// Reset pagination when query changes
watch(
    () => query.value,
    () => {
        visibleCount.value = PAGE_SIZE;
    }
);

const toggleCostumer = (costumer: ICostumer) => {
    selectedCostumerId.value = isSelected(costumer.id) ? null : costumer.id;
};
const onCheckboxChange = (ev: any, costumer: ICostumer) => {
    const checked = ev?.detail?.checked as boolean;
    selectedCostumerId.value = checked ? costumer.id : null;
};

// const overrideAlert = async () => {
//     const alert = await alertController.create({
//         header: "Assegna clienti all'area",
//         message: `Alcuni dei clienti selezionati sono già assegnati ad un altra area. Sovrascrivere?`,
//         buttons: [
//             {
//                 text: "Annulla",
//                 role: "cancel",
//                 cssClass: "archive-text-medium",
//             },
//             {
//                 text: "Sovrascrivi",
//                 cssClass: "archive-text-danger",
//                 handler: async () => {
//                     await costumerStore.assignCostumersToZone(
//                         selectedCostumers.value.map((c) => c.id),
//                         props.zone.id
//                     );
//                     return modalController.dismiss(null, "confirm");
//                 },
//             },
//         ],
//     });
//     await alert.present();
// };
const cancel = () => {
    return modalController.dismiss(null, "cancel");
};
const submit = async () => {
    const id = selectedCostumerId.value;
    const selected = id != null ? costumerStore.getCustomerById(id) : null;
    if (!selected) {
        return modalController.dismiss(null, "cancel");
    }
    if (props.onConfirm) {
        const confirmed = await props.onConfirm(selected);
        if (!confirmed) {
            return modalController.dismiss(null, "cancel");
        }
    }
    return modalController.dismiss(selected, "confirm");
};

onMounted(() => {});
</script>
<template>
    <ion-header>
        <ion-toolbar>
            <ion-buttons slot="start">
                <ion-button color="medium" @click="cancel">Annulla</ion-button>
            </ion-buttons>
            <ion-title>Seleziona Cliente</ion-title>
            <ion-buttons slot="end">
                <ion-button @click="submit" :strong="true">Conferma</ion-button>
            </ion-buttons>
        </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding" style="--background: var(--ion-item-background)">
        <ion-searchbar
            placeholder="Cerca cliente (nome, indirizzo, città)"
            v-model="query"
            :debounce="150"
            class="ion-margin-bottom" />
        <ion-list>
            <template v-if="!isLoading">
                <ion-item
                    v-for="customer in visibleCustomers"
                    :key="customer.id"
                    button
                    @click="toggleCostumer(customer)"
                    :detail="false"
                    :class="{ 'ion-activated': isSelected(customer.id) }">
                    <ion-checkbox
                        :checked="isSelected(customer.id)"
                        @ionChange.stop="onCheckboxChange($event, customer)"
                        @click.stop
                        :aria-label="`select costumer-${customer.id}`"
                        slot="start"></ion-checkbox>
                    <ion-label>
                        <ion-label>{{ customer.business_name }}</ion-label>
                    </ion-label>
                </ion-item>
                <ion-infinite-scroll v-if="canLoadMore" threshold="100px" @ionInfinite="onInfinite">
                    <ion-infinite-scroll-content loading-spinner="bubbles" loading-text="Caricamento..." />
                </ion-infinite-scroll>
            </template>
            <template v-else>
                <ion-item class="costumer-list-item" v-for="_ in [1, 2, 3, 4, 5, 6, 7, 8, 9]">
                    <ion-label>
                        <h3>
                            <ion-skeleton-text :animated="true" style="width: 80%"></ion-skeleton-text>
                        </h3>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 60%"></ion-skeleton-text>
                        </p>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 30%"></ion-skeleton-text>
                        </p>
                    </ion-label>
                </ion-item>
            </template>
        </ion-list>
    </ion-content>
</template>

<style scoped></style>
